import React from 'react';
import type { FailureDetail } from '../types';

interface LangfuseValueProps {
  children: React.ReactNode;
  href: string | null;
}

// The value itself links out to Langfuse (new tab) when the call was traced.
const LangfuseValue = ({ children, href }: LangfuseValueProps) => (href ? (
  <a
    className="tw-text-spa-accent-text hover:tw-text-spa-accent-text-hover"
    href={href}
    rel="noreferrer"
    target="_blank"
  >
    {children}
    <span aria-hidden="true" className="tw-ml-0.5">↗</span>
  </a>
) : <>{children}</>);

// Prompt, locale, and the two LLM calls behind the row, as a quiet key/value footer.
const Provenance = ({ detail }: { detail: FailureDetail }) => {
  const { langfuse } = detail;
  const rows: Array<[string, React.ReactNode]> = [
    ['Prompt', detail.promptName && `${detail.promptName} v${detail.promptVersion ?? '?'}`],
    ['Locale', detail.locale],
    ['Generation call', detail.llmCompletionId && (
      <LangfuseValue href={langfuse.generationTraceUrl}>{`#${detail.llmCompletionId}`}</LangfuseValue>
    )],
    ['Check call', detail.checkLlmCompletionId && (
      <LangfuseValue href={langfuse.checkTraceUrl}>{`#${detail.checkLlmCompletionId}`}</LangfuseValue>
    )],
    ['Langfuse session', detail.sessionId && (
      <LangfuseValue href={langfuse.sessionUrl}>
        <code className="tw-text-[0.6875rem]">{detail.sessionId}</code>
      </LangfuseValue>
    )],
  ];

  return (
    <dl className="tw-m-0 tw-border-0 tw-border-t tw-border-solid tw-border-spa-border tw-pt-3">
      {rows.map(([label, value]) => (
        <div key={label} className="tw-flex tw-gap-4 tw-py-1 tw-text-xs">
          <dt className="tw-w-32 tw-shrink-0 tw-text-spa-tertiary">{label}</dt>
          <dd className="tw-m-0 tw-min-w-0 tw-break-all tw-text-spa-primary">{value || '—'}</dd>
        </div>
      ))}
    </dl>
  );
};

export default Provenance;
